import {
  type ReportCardComposerContext,
  type ReportCardView,
  formatReportCardTime,
  reportCardThumbUrl,
} from '@/lib/report_cards';
import { Check, Loader2, Pill, Send, Utensils, X } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Modal } from './Modal';

/**
 * Staff report-card composer (pet report cards, migration 0093).
 *
 * Opens as a modal from the booking detail page and the Today board. Loads
 * the composer context (tenant label set, default sign-off, today's
 * medication administrations, feeding notes) and the booking's photo
 * timeline in parallel, then lets staff:
 *   - pick ONE headline mood label (tenant-configured set)
 *   - write free-text notes
 *   - select ANY number of photos (no cap — deliberate)
 *   - toggle the auto-pulled medications + feeding sections
 *   - edit the sign-off line
 * and POST /api/bookings/:id/report-cards. On success calls `onSent` with the
 * created card and closes.
 */

interface TimelinePhoto {
  id: string;
  fileId: string;
  caption: string | null;
  takenAt: string;
}

export interface ReportCardComposerProps {
  bookingId: string;
  petName?: string | null;
  onClose: () => void;
  onSent?: (card: ReportCardView) => void;
}

type LoadState =
  | { status: 'loading' }
  | { status: 'error'; message: string }
  | { status: 'ready'; context: ReportCardComposerContext; photos: TimelinePhoto[] };

export function ReportCardComposer({
  bookingId,
  petName,
  onClose,
  onSent,
}: ReportCardComposerProps) {
  const [load, setLoad] = useState<LoadState>({ status: 'loading' });
  const [moodLabel, setMoodLabel] = useState<string | null>(null);
  const [notes, setNotes] = useState('');
  const [selected, setSelected] = useState<Set<string>>(() => new Set());
  const [includeMedications, setIncludeMedications] = useState(true);
  const [includeFeeding, setIncludeFeeding] = useState(true);
  const [signOff, setSignOff] = useState('');
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [ctxRes, photosRes] = await Promise.all([
          fetch(`/api/bookings/${encodeURIComponent(bookingId)}/report-cards/context`, {
            credentials: 'include',
          }),
          fetch(`/api/bookings/${encodeURIComponent(bookingId)}/photos?limit=200`, {
            credentials: 'include',
          }),
        ]);
        if (!ctxRes.ok) throw new Error(`Could not load report card details (${ctxRes.status})`);
        const context = (await ctxRes.json()) as ReportCardComposerContext;
        const photos = photosRes.ok
          ? (((await photosRes.json()) as { photos: TimelinePhoto[] }).photos ?? [])
          : [];
        if (cancelled) return;
        setSignOff(context.settings.signOffLine ?? '');
        setLoad({ status: 'ready', context, photos });
      } catch (err) {
        if (cancelled) return;
        setLoad({ status: 'error', message: (err as Error).message });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [bookingId]);

  const togglePhoto = useCallback((id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const allSelected = useMemo(
    () => load.status === 'ready' && load.photos.length > 0 && selected.size === load.photos.length,
    [load, selected],
  );

  const toggleAll = () => {
    if (load.status !== 'ready') return;
    setSelected(allSelected ? new Set() : new Set(load.photos.map((p) => p.id)));
  };

  const hasContent =
    !!moodLabel ||
    notes.trim().length > 0 ||
    selected.size > 0 ||
    (load.status === 'ready' &&
      ((includeMedications && load.context.medications.length > 0) ||
        (includeFeeding && !!load.context.feedingNotes)));

  const send = async () => {
    if (load.status !== 'ready' || sending) return;
    setSending(true);
    setSendError(null);
    try {
      const res = await fetch(`/api/bookings/${encodeURIComponent(bookingId)}/report-cards`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          moodLabel,
          notes: notes.trim().length > 0 ? notes : null,
          photoIds: Array.from(selected),
          includeMedications,
          includeFeeding,
          signOff: signOff.trim().length > 0 ? signOff.trim() : null,
        }),
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { issues?: string[]; error?: string };
        throw new Error(body.issues?.[0] ?? body.error ?? `Send failed (${res.status})`);
      }
      const body = (await res.json()) as { reportCard: ReportCardView };
      onSent?.(body.reportCard);
      onClose();
    } catch (err) {
      setSendError((err as Error).message);
    } finally {
      setSending(false);
    }
  };

  return (
    <Modal onClose={onClose} aria-label="Send report card" maxWidth="max-w-2xl">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-lg font-semibold">Report card{petName ? ` for ${petName}` : ''}</h2>
          <p className="mt-0.5 text-sm text-muted-foreground">
            Tell the owner how the day went. Photos, medication and feeding are pulled in for you.
          </p>
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="rounded-md p-1 text-muted-foreground hover:bg-muted"
        >
          <X className="size-4" strokeWidth={1.75} />
        </button>
      </div>

      {load.status === 'loading' && (
        <div
          className="mt-6 flex items-center gap-2 text-sm text-muted-foreground"
          data-testid="report-card-composer-loading"
        >
          <Loader2 className="size-4 animate-spin" strokeWidth={1.75} />
          Loading…
        </div>
      )}

      {load.status === 'error' && (
        <p className="mt-6 text-sm text-destructive" role="alert">
          {load.message}
        </p>
      )}

      {load.status === 'ready' && !load.context.sendable && (
        <p className="mt-6 text-sm text-muted-foreground" role="alert">
          Report cards can only be sent for active boarding stays and daycare visits.
        </p>
      )}

      {load.status === 'ready' && load.context.sendable && (
        <div className="mt-5 space-y-5" data-testid="report-card-composer">
          <fieldset>
            <legend className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              How was the day?
            </legend>
            <div className="mt-2 flex flex-wrap gap-2">
              {load.context.settings.moodLabels.map((label) => {
                const active = moodLabel === label;
                return (
                  <button
                    key={label}
                    type="button"
                    aria-pressed={active}
                    onClick={() => setMoodLabel(active ? null : label)}
                    className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-sm ${
                      active
                        ? 'border-primary bg-primary text-primary-foreground'
                        : 'border-border bg-background hover:bg-muted'
                    }`}
                  >
                    {active && <Check className="size-3.5" strokeWidth={2} />}
                    {label}
                  </button>
                );
              })}
            </div>
          </fieldset>

          <div>
            <label
              htmlFor={`report-card-notes-${bookingId}`}
              className="text-xs font-medium uppercase tracking-wide text-muted-foreground"
            >
              Notes
            </label>
            <textarea
              id={`report-card-notes-${bookingId}`}
              value={notes}
              onChange={(e) => setNotes(e.currentTarget.value)}
              rows={4}
              maxLength={4000}
              placeholder="Played fetch with Bella, ate every meal, settled quickly at bedtime…"
              className="mt-1.5 w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
            />
          </div>

          <div>
            <div className="flex items-center justify-between">
              <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                Photos{' '}
                <span className="num">
                  ({selected.size}/{load.photos.length})
                </span>
              </span>
              {load.photos.length > 0 && (
                <button
                  type="button"
                  onClick={toggleAll}
                  className="text-xs font-medium text-primary hover:underline"
                >
                  {allSelected ? 'Clear all' : 'Select all'}
                </button>
              )}
            </div>
            {load.photos.length === 0 ? (
              <p className="mt-1.5 text-sm text-muted-foreground">
                No photos on this stay yet. Post some to the timeline first.
              </p>
            ) : (
              <div className="mt-2 grid grid-cols-4 gap-2 sm:grid-cols-5">
                {load.photos.map((photo) => {
                  const active = selected.has(photo.id);
                  return (
                    <button
                      key={photo.id}
                      type="button"
                      aria-pressed={active}
                      aria-label={photo.caption ? `Photo: ${photo.caption}` : 'Photo'}
                      data-testid={`report-card-photo-${photo.id}`}
                      onClick={() => togglePhoto(photo.id)}
                      className={`relative overflow-hidden rounded-md border-2 ${
                        active ? 'border-primary' : 'border-transparent'
                      }`}
                    >
                      <img
                        src={reportCardThumbUrl(photo.fileId)}
                        alt={photo.caption ?? ''}
                        loading="lazy"
                        className="aspect-square w-full bg-muted object-cover"
                      />
                      {active && (
                        <span className="absolute right-1 top-1 rounded-full bg-primary p-0.5 text-primary-foreground">
                          <Check className="size-3" strokeWidth={2.5} />
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <label className="flex items-start gap-2 rounded-md border border-border p-3 text-sm">
              <input
                type="checkbox"
                className="mt-0.5 size-4"
                checked={includeMedications}
                onChange={(e) => setIncludeMedications(e.currentTarget.checked)}
                disabled={load.context.medications.length === 0}
              />
              <span>
                <span className="flex items-center gap-1.5 font-medium">
                  <Pill className="size-3.5" strokeWidth={1.75} />
                  Medication today
                </span>
                {load.context.medications.length === 0 ? (
                  <span className="mt-1 block text-xs text-muted-foreground">
                    Nothing administered today.
                  </span>
                ) : (
                  <ul className="mt-1 space-y-0.5 text-xs text-muted-foreground">
                    {load.context.medications.map((m) => (
                      <li key={`${m.medicationName}-${m.administeredAt}`}>
                        {m.medicationName}
                        {m.dose ? ` (${m.dose})` : ''} at{' '}
                        <span className="num">{formatReportCardTime(m.administeredAt)}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </span>
            </label>
            <label className="flex items-start gap-2 rounded-md border border-border p-3 text-sm">
              <input
                type="checkbox"
                className="mt-0.5 size-4"
                checked={includeFeeding}
                onChange={(e) => setIncludeFeeding(e.currentTarget.checked)}
                disabled={!load.context.feedingNotes}
              />
              <span>
                <span className="flex items-center gap-1.5 font-medium">
                  <Utensils className="size-3.5" strokeWidth={1.75} />
                  Feeding
                </span>
                {/* Migration 0101 — logged meal outcomes lead; plan text follows. */}
                {load.context.feedingLog && load.context.feedingLog.length > 0 && (
                  <ul
                    className="mt-1 space-y-0.5 text-xs text-muted-foreground"
                    data-testid="report-card-feeding-log"
                  >
                    {load.context.feedingLog.map((e) => (
                      <li key={e.id}>
                        <span className="num">
                          {e.slot.charAt(0).toUpperCase() + e.slot.slice(1)}
                          {e.slotTime ? ` ${e.slotTime}` : ''}
                        </span>
                        : {e.outcome}
                        {e.notes ? ` — ${e.notes}` : ''}
                      </li>
                    ))}
                  </ul>
                )}
                <span className="mt-1 block whitespace-pre-line text-xs text-muted-foreground">
                  {load.context.feedingLog && load.context.feedingLog.length > 0
                    ? (load.context.feedingPlanText ?? '')
                    : (load.context.feedingNotes ?? 'No feeding notes on file.')}
                </span>
              </span>
            </label>
          </div>

          <div>
            <label
              htmlFor={`report-card-signoff-${bookingId}`}
              className="text-xs font-medium uppercase tracking-wide text-muted-foreground"
            >
              Sign-off
            </label>
            <input
              id={`report-card-signoff-${bookingId}`}
              type="text"
              value={signOff}
              onChange={(e) => setSignOff(e.currentTarget.value)}
              maxLength={300}
              placeholder="See you tomorrow — the team"
              className="mt-1.5 w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
            />
          </div>

          {sendError && (
            <p className="text-sm text-destructive" role="alert">
              {sendError}
            </p>
          )}

          <div className="flex items-center justify-end gap-2 border-t border-border pt-4">
            <button
              type="button"
              onClick={onClose}
              className="rounded-md border border-border bg-background px-3 py-2 text-sm font-medium hover:bg-muted"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => void send()}
              disabled={!hasContent || sending}
              className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-2 text-sm font-medium text-primary-foreground hover:opacity-90 disabled:opacity-50"
            >
              {sending ? (
                <Loader2 className="size-4 animate-spin" strokeWidth={1.75} />
              ) : (
                <Send className="size-4" strokeWidth={1.75} />
              )}
              Send report card
            </button>
          </div>
        </div>
      )}
    </Modal>
  );
}
